import React from 'react';

const ABOUT_PILLARS = [
  {
    title: 'Mission',
    description:
      'Make clinical trial operations clear, accountable, and compliant so teams spend less time on admin and more time on patients.',
  },
  {
    title: 'Vision',
    description:
      'A world where every study runs with instantaneous visibility and audit-ready documentation from day one.',
  },
  {
    title: 'Purpose',
    description:
      'Empower sites, sponsors, and CROs with tools that reduce friction, improve collaboration, and elevate trial outcomes.',
  },
];

const TEAM_FOCUS = [
  {
    title: 'Clinical operations',
    description: 'Site coordinators, quality controllers, compliance officers, and trial managers who have lived the workflow pain points.',
  },
  {
    title: 'Compliance & quality',
    description: 'GCP-minded experts ensuring documentation and oversight are inspection-ready.',
  },
  {
    title: 'Product & engineering',
    description: 'Builders focused on reliable, secure, and intuitive systems for study execution.',
  },
];

const LEADERSHIP_MEMBERS = [
  {
    name: 'Guy Neff, MD, MBA',
    role: 'Chief Executive Officer',
    bio: 'Dr. Guy Neff is a seasoned clinical research executive with decades of experience bridging clinical care, regulatory compliance, and operational excellence. He leads Phennx with a commitment to improving patient outcomes, enhancing site operations, and fostering innovation in clinical trials.',
    image: '/team/gneff.png',
  },
  {
    name: 'Matthew Kongsiri, MS',
    role: 'Chief Technology Officer',
    bio: 'Matthew Kongsiri is a technology leader with a deep passion for building secure, scalable, and intuitive platforms to streamline clinical research. As CTO, he combines hands-on engineering experience with a vision for powering collaboration and compliance across every phase of the clinical trial process.',
    image: '/team/mkongsiri.png',
  },
];

const TEAM_MEMBERS = [
  {
    name: 'Patrick Riley',
    role: 'Engineering',
    bio: 'Patrick Riley is a software engineer at Phennx. Specializing in feature development and system reliability, he translates complex protocol requirements into streamlined, dependable workflows. He builds with a focus on quality, precision, and creating tools that clinical teams can trust.',
    image: '/team/priley.png'
  }
];

const ADVISORS = [
  {
    name: 'Johanna Valentin, MBA',
    role: 'Advisor',
    bio: '',
    image: '/team/jvalentin.png'
  },
  {
    name: 'Julissa Taveras, MSN, APRN-BC',
    role: 'Advisor',
    bio: '',
    image: '/team/jtaveras.png'
  },
  {
    name: 'Ivan Biletskyy, CPA',
    role: 'Advisor',
    bio: '',
    image: '/team/ibiletskyy.png'
  },
  {
    name: 'Judy Seymour',
    role: 'Advisor',
    bio: '',
    image: '/team/jseymour.png'
  },
  {
    name: 'Kaitlyn Woolard',
    role: 'Advisor',
    bio: '',
    image: '/team/kwoolard.png'
  }
];

export default function AboutSection() {
  return (
    <section className="section about-section" id="about" aria-label="About Phennx">
      <div className="section-inner">
        <div className="section-header">
          <h2 className="section-title">About Phennx</h2>
          <p className="section-subtitle">
            We are focused on bringing clarity to clinical trial operations through structured workflows, accountability,
            and AI support that keeps teams in control.
          </p>
        </div>

        <div className="about-grid">
          <div className="about-pillars" role="list" aria-label="Mission, vision, and purpose">
            {ABOUT_PILLARS.map((pillar) => (
              <div key={pillar.title} className="about-card" role="listitem">
                <div className="about-card__title">{pillar.title}</div>
                <div className="about-card__text">{pillar.description}</div>
              </div>
            ))}
          </div>

          <div className="about-team">
            <div className="about-team__title">Our team</div>
            <p className="about-team__lead">
              We blend clinical trial operations, compliance, and modern software engineering to deliver tools that scale
              across studies without losing the human touch.
            </p>
            <div className="about-team__list" role="list" aria-label="Team focus areas">
              {TEAM_FOCUS.map((item) => (
                <div key={item.title} className="about-team__item" role="listitem">
                  <div className="about-team__item-title">{item.title}</div>
                  <div className="about-team__item-text">{item.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="about-headshots">
          <div className="about-headshots__header">
            <h3 className="about-headshots__title">Leadership</h3>
            <p className="about-headshots__subtitle">
              A team grounded in clinical operations, compliance, and thoughtful product design.
            </p>
          </div>

          <div className="about-headshots__grid" role="list" aria-label="Team leadership">
            {LEADERSHIP_MEMBERS.map((member) => (
              <div key={member.name} className="headshot-card" role="listitem">
                <div className="headshot-card__image">
                  <img src={member.image} alt={`${member.name} headshot`} loading="lazy" />
                </div>
                <div className="headshot-card__name">{member.name}</div>
                <div className="headshot-card__role">{member.role}</div>
                <div className="headshot-card__bio">{member.bio}</div>
              </div>
            ))}
          </div>

          <div className="about-headshots__header mt-4">
            <h3 className="about-headshots__title">Team members</h3>
            <p className="about-headshots__subtitle">
              The clinicians, operators, and engineers delivering day-to-day execution.
            </p>
          </div>

          {TEAM_MEMBERS.length === 0 ? (
            <p className="about-headshots__subtitle">Team member profiles will be added soon.</p>
          ) : (
            <div className="about-headshots__grid" role="list" aria-label="Team members">
              {TEAM_MEMBERS.map((member) => (
                <div key={member.name} className="headshot-card" role="listitem">
                  <div className="headshot-card__image">
                    <img src={member.image} alt={`${member.name} headshot`} loading="lazy" />
                  </div>
                  <div className="headshot-card__name">{member.name}</div>
                  <div className="headshot-card__role">{member.role}</div>
                  <div className="headshot-card__bio">{member.bio}</div>
                </div>
              ))}
            </div>
          )}

          <div className="about-headshots__header mt-4">
            <h3 className="about-headshots__title">Advisors</h3>
            <p className="about-headshots__subtitle">
              Seasoned leaders guiding our clinical and operational strategy.
            </p>
          </div>

          {ADVISORS.length === 0 ? (
            <p className="about-headshots__subtitle">Advisor profiles will be added soon.</p>
          ) : (
            <div className="about-headshots__grid" role="list" aria-label="Advisors">
              {ADVISORS.map((member) => (
                <div key={member.name} className="headshot-card" role="listitem">
                  <div className="headshot-card__image">
                    <img src={member.image} alt={`${member.name} headshot`} loading="lazy" />
                  </div>
                  <div className="headshot-card__name">{member.name}</div>
                  <div className="headshot-card__role">{member.role}</div>
                  <div className="headshot-card__bio">{member.bio}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
